import React from 'react';
import { Zap, BookOpen, LineChart, ClipboardList, Layers, Settings, Key,
  Activity, TrendingUp, Table2, Radio, Percent, Gauge, BarChart3, Waves, Compass, LayoutDashboard, CandlestickChart, Crosshair
} from 'lucide-react';
var MOBILE_IDS = ['jpsignal', 'quantiply', 'settings'];
// EXPORTED so Settings can build its visibility toggles from the same list.
// Keeping a second hand-maintained copy there meant a new page needed adding
// in two places and would silently have no toggle if you forgot one.
export var navItems = [
  {
    section: 'Overview',
    items: [
      { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
{ id: 'workspace', label: 'Workspace', icon: LayoutDashboard },
    ]
  },
  {
    section: 'Strategy',
    items: [
      { id: 'jpsignal',   label: 'JP Signal',     icon: Radio },
      { id: 'quantiply',  label: 'Quantiply',     icon: Zap },
      { id: 'amb', label: 'AMB', icon: Activity },
      { id: 'amb2', label: 'AMB 2', icon: Activity },
      { id: 'amb3', label: 'AMB 3', icon: Activity },
      { id: 'super_straddle', label: 'Super Straddle', icon: Activity },
      { id: 'super_straddle_2', label: 'Super Straddle 2', icon: Activity },
      { id: 'super_straddle_3', label: 'Super Straddle 3', icon: Activity },
    ]
  },
  {
    section: 'Market',
    items: [
      { id: 'optionchain', label: 'Option Chain', icon: Table2 },
      { id: 'ivsurface', label: 'IV Surface', icon: LineChart },
      { id: 'hybrid',      label: 'Hybrid Conviction', icon: Gauge },
      { id: 'gex',         label: 'Gamma Exposure', icon: BarChart3 },
      { id: 'positioning', label: 'Positioning', icon: Compass },
      { id: 'oi',          label: 'Open Interest', icon: BarChart3 },
      { id: 'oiride',      label: 'OI Ride', icon: Waves },
      { id: 'oirided',     label: 'OI Ride D', icon: Waves },
      { id: 'oirided1',    label: 'OI Ride D-1', icon: Waves },
      { id: 'oirider',     label: 'OI Ride-R', icon: Waves },
      { id: 'paper_journal', label: 'Paper Journal', icon: ClipboardList },
      { id: 'oiwalls',     label: 'OI Walls', icon: Layers },
      { id: 'oiwalls3x',   label: 'OI Walls 3x', icon: Layers },
      { id: 'oiwalls2x',   label: 'OI Walls 2X', icon: Layers },
      { id: 'oiwalls_live', label: 'OI Walls LIVE', icon: Layers },
      { id: 'oiwalls3x_live', label: 'OI Walls 3x LIVE', icon: Layers },
      { id: 'gexwalls_live', label: 'GEX Walls LIVE', icon: Crosshair },
{ id: 'oiride_d_live', label: 'OI Ride D LIVE', icon: Crosshair },
{ id: 'oiride_r_live', label: 'OI Ride-R LIVE', icon: Crosshair },
{ id: 'directional_tv', label: 'Directional TV', icon: TrendingUp },
{ id: 'directional_tv1', label: 'Directional TV-1', icon: TrendingUp },
      { id: 'gexwalls',    label: 'GEX Walls', icon: Crosshair },
      { id: 'straddle',    label: 'Straddle Tracker', icon: Waves },
      { id: 'journal',     label: 'Trade Journal', icon: BookOpen },
    ]
  },
  {
    section: 'System',
    items: [
      { id: 'logs',      label: 'Logs',        icon: BookOpen },
      { id: 'orders',    label: 'Orders',      icon: ClipboardList },
      { id: 'token',     label: 'Token Setup', icon: Key },
      { id: 'settings',  label: 'Settings',    icon: Settings },
    ]
  }
];

// Nav visibility arrives as a PROP from App, which owns the single fetch.
// Each of these components used to run its own copy of the hook — four
// independent fetches, four pieces of state — so the bottom bar and the swipe
// order could settle on different lists depending on which response landed
// first. One source of truth removes the race entirely.

function applyHidden(nav, hidden) {
  if (!hidden.length) return nav;
  return nav.map(function (sec) {
    return {
      section: sec.section,
      items: sec.items.filter(function (it) { return hidden.indexOf(it.id) === -1; }),
    };
  }).filter(function (sec) { return sec.items.length > 0; });   // drop empty sections
}

export default function Sidebar({ activePage, setActivePage, mobileOpen, isMobile, hidden }) {
  hidden = hidden || [];
  var sidebarClass = 'sidebar';
  if (isMobile) {
    sidebarClass += mobileOpen ? ' mobile-open' : ' mobile-closed';
  }
  // Mobile shows a reduced set (MOBILE_IDS) and now honours the hidden list on
  // top of it, so the Settings toggles apply on every surface. Settings itself
  // is never in `hidden`, so the way back always survives.
  var filteredNav = isMobile
    ? applyHidden(navItems.map(function(section) {
        return {
          section: section.section,
          items: section.items.filter(function(item) { return MOBILE_IDS.indexOf(item.id) >= 0; })
        };
      }).filter(function(section) { return section.items.length > 0; }), hidden)
    : applyHidden(navItems, hidden);
  return (
    <div className={sidebarClass}>
      <div className="sidebar-logo" onClick={function() { setActivePage('dashboard'); }}>
        <img src="/algrok-logo.jpg" alt="AK" style={{ width: 30, height: 30, borderRadius: 8 }} />
        <span className="logo-text">Algrok</span>
      </div>
      <nav className="sidebar-nav">
        {filteredNav.map(function(section) {
          return (
            <React.Fragment key={section.section}>
              {!isMobile && <div className="nav-section-label">{section.section}</div>}
              {section.items.map(function(item) {
                var Icon = item.icon;
                return (
                  <div
                    key={item.id}
                    className={'nav-item' + (activePage === item.id ? ' active' : '')}
                    onClick={function() { setActivePage(item.id); }}
                  >
                    <Icon size={isMobile ? 20 : 17} />
                    <span className="nav-label">{item.label}</span>
                  </div>
                );
              })}
            </React.Fragment>
          );
        })}
      </nav>
    </div>
  );
}
